/**
 * The one place that talks to the network.
 *
 * Every path is relative. In dev, Vite proxies /api to the backend; in
 * production FastAPI serves this bundle from the same origin as the API. So the
 * frontend never needs to know a backend URL, there is no VITE_API_URL to get
 * wrong per environment, and cookies are same-origin everywhere.
 */

import { useAccessStore } from "@/stores/access-store";

const BASE = "/api/v1";

/** The error code the server answers any call with once the caller's account is turned off. */
export const DEACTIVATED = "user_deactivated";

/** Said in place of a validation error the server sent as data, not a sentence. */
export const NOT_ACCEPTED = "The server could not accept this request. Check what you entered and try again.";

/**
 * The project the page is in (`/p/<slug>/...`), set once by main.tsx before the
 * router starts. Feature code keeps calling `/experiments`; while a project is
 * set, every project-scoped path goes out as `/projects/<slug>/experiments`.
 * Switching project is a full page load, so this never changes under a mounted
 * page and the query cache never mixes two projects.
 */
let projectSlug: string | null = null;

export function setProjectSlug(slug: string | null) {
  projectSlug = slug;
}

export function getProjectSlug(): string | null {
  return projectSlug;
}

/** Paths that belong to the whole workbench, never to one project. `/projects`
 *  covers the list and each project's own detail and members; `/users` is the
 *  people picker for adding members; `/admin` is workbench user administration. */
const GLOBAL = [
  "/admin",
  "/auth",
  "/users",
  "/projects",
  "/connections",
  "/credentials",
  "/trace-sources",
  "/connectors",
];

const isGlobal = (path: string) =>
  GLOBAL.some((g) => path === g || path.startsWith(`${g}/`) || path.startsWith(`${g}?`));

/** `path` as sent: under the current project unless it is global or no project is set. */
export function scoped(path: string): string {
  return projectSlug && !isGlobal(path) ? `/projects/${encodeURIComponent(projectSlug)}${path}` : path;
}

/** An error carrying the HTTP status, so callers can branch on 404 vs 409, and
 *  the server's machine-readable `code` when it sent one (`user_deactivated`). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: string,
    readonly code: string | null = null,
  ) {
    super(detail);
    this.name = "ApiError";
  }

  /** True when the server rejected the request for a reason the user can fix —
   *  a broken business rule, not a bug. Worth showing verbatim; a 500's message
   *  never is. */
  get isUserFixable(): boolean {
    return this.status === 409 || this.status === 422 || this.status === 400;
  }
}

/** Paths that must never trigger a refresh: the refresh itself (it would loop)
 *  and logout (a 401 there means "already signed out", which is the goal). */
const NO_REFRESH = ["/auth/refresh", "/auth/logout"];

/** One refresh at a time: a page firing ten queries on an expired session
 *  sends one POST /auth/refresh, and all ten wait for it. */
let refreshing: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, { method: "POST", credentials: "include" })
    .then(async (r) => {
      // A deactivated account cannot refresh either: say so instead of a sign-in loop.
      if (!r.ok) await parse(r).catch(() => undefined);
      return r.ok;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response = await send(path, init);
  // The session cookie expired but the refresh cookie may not have: renew it
  // once and retry, so nobody is sent to the sign-in screen mid-task. Only if
  // the refresh also fails does the 401 reach the caller.
  if (response.status === 401 && !NO_REFRESH.includes(path) && (await refreshSession())) {
    response = await send(path, init);
  }
  return parse<T>(response);
}

function send(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${BASE}${scoped(path)}`, {
    // Send cookies. The template has no auth yet, but wiring this in from the
    // start means dropping in a session-cookie auth block later changes nothing
    // in this file.
    credentials: "include",
    ...init,
    headers: {
      ...(init?.body instanceof FormData ? {} : { "Content-Type": "application/json" }),
      ...init?.headers,
    },
  });
}

async function parse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // The backend returns {"detail": "..."} for its own errors; FastAPI's
    // validation errors put a list there instead.
    let detail = response.statusText;
    let code: string | null = null;
    try {
      const body = await response.json();
      // FastAPI's own 422s send a list of field errors: never show that raw.
      detail = typeof body.detail === "string" ? body.detail : NOT_ACCEPTED;
      code = typeof body.code === "string" ? body.code : null;
    } catch {
      /* a non-JSON error body (a proxy timeout, say) — keep the status text */
    }
    // An admin turned this account off mid-session: every page gives way to one
    // screen saying so (main.tsx), whichever call noticed first.
    if (response.status === 403 && code === DEACTIVATED) useAccessStore.getState().markDeactivated();
    throw new ApiError(response.status, detail, code);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

type QueryValue = string | number | boolean | null | undefined;

/** `withQuery("/experiments", { agent_id: "a", status: undefined })` →
 *  `/experiments?agent_id=a`. Empty values are dropped, so callers can pass
 *  optional filters straight through without building the string by hand. A
 *  list repeats the param (`{ type: ["A", "B"] }` → `type=A&type=B`). */
export function withQuery(path: string, params: Record<string, QueryValue | QueryValue[]>): string {
  const search = new URLSearchParams();
  for (const [key, given] of Object.entries(params)) {
    for (const value of Array.isArray(given) ? given : [given]) {
      if (value === undefined || value === null || value === "") continue;
      search.append(key, String(value));
    }
  }
  const query = search.toString();
  return query ? `${path}?${query}` : path;
}

export const api = {
  /** The browser-visible URL of an API path, for the one thing fetch cannot do:
   *  a plain `<a href download>` (the sample CSV per dataset type). It is still
   *  relative, so the same-origin rule above holds. Nothing else should need it. */
  url: (path: string) => `${BASE}${scoped(path)}`,
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    // The key is omitted rather than set to undefined: with
    // `exactOptionalPropertyTypes` those are different types, and RequestInit
    // does not accept an explicit undefined body.
    request<T>(path, {
      method: "POST",
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: (path: string) => request<void>(path, { method: "DELETE" }),
  /** POST something whose answer is a redirect the SPA should NOT follow.
   *  The `auth` block answers logout with a 303 to the frontend; letting fetch
   *  chase it would hand us an HTML page to parse as JSON. */
  postExpectingRedirect: async (path: string): Promise<void> => {
    const response = await fetch(`${BASE}${scoped(path)}`, {
      method: "POST",
      credentials: "include",
      redirect: "manual",
    });
    // An opaque redirect reports status 0 and type "opaqueredirect" — that is
    // the success case here, not a failure.
    if (!response.ok && response.type !== "opaqueredirect" && response.status !== 0) {
      throw new ApiError(response.status, response.statusText);
    }
  },
};
